import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksApi, projectsApi, milestonesApi, attachmentsApi } from '../api/client';
import { useUIStore } from '../store';
import type { Task, Project, Milestone } from '../types';
import {
  X,
  Calendar,
  Clock,
  Flag,
  Folder,
  Target,
  Paperclip,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

export function TaskModal() {
  const queryClient = useQueryClient();
  const { isTaskModalOpen, closeTaskModal, selectedTaskId } = useUIStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  const [form, setForm] = useState<Partial<Task>>({
    title: '',
    description: '',
    projectId: '',
    milestoneId: null,
    parentId: null,
    status: 'todo',
    priority: 'medium',
    startTime: null,
    endTime: null,
    dueDate: null,
    reminderMinutes: null,
  });

  // 获取项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
  });

  // 获取里程碑列表
  const { data: milestonesData } = useQuery({
    queryKey: ['milestones', form.projectId],
    queryFn: () =>
      form.projectId
        ? milestonesApi.getByProject(form.projectId)
        : Promise.resolve({ success: true, data: [] }),
    enabled: !!form.projectId,
  });

  // 获取任务详情（编辑模式）
  const { data: taskData } = useQuery({
    queryKey: ['task', selectedTaskId],
    queryFn: () =>
      selectedTaskId ? tasksApi.getById(selectedTaskId) : Promise.resolve(null),
    enabled: !!selectedTaskId,
  });

  // 获取附件列表
  const { data: attachmentsData } = useQuery({
    queryKey: ['attachments', selectedTaskId],
    queryFn: () =>
      selectedTaskId
        ? attachmentsApi.getByTask(selectedTaskId)
        : Promise.resolve({ success: true, data: [] }),
    enabled: !!selectedTaskId,
  });

  const projects = projectsData?.success ? projectsData.data : [];
  const milestones = milestonesData?.success ? milestonesData.data : [];
  const attachments = attachmentsData?.success ? attachmentsData.data : [];

  // 创建任务
  const createMutation = useMutation({
    mutationFn: (data: Partial<Task>) => tasksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      closeTaskModal();
    },
  });

  // 更新任务
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Task> }) =>
      tasksApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', selectedTaskId] });
      closeTaskModal();
    },
  });

  // 上传附件
  const uploadMutation = useMutation({
    mutationFn: ({ taskId, file }: { taskId: string; file: File }) =>
      attachmentsApi.upload(taskId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', selectedTaskId] });
      setUploadingFiles([]);
    },
  });

  // 删除附件
  const deleteAttachmentMutation = useMutation({
    mutationFn: (id: string) => attachmentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', selectedTaskId] });
    },
  });

  // 加载任务数据（编辑模式）
  useEffect(() => {
    if (taskData?.success && taskData.data) {
      const task = taskData.data;
      setForm({
        title: task.title,
        description: task.description,
        projectId: task.projectId,
        milestoneId: task.milestoneId,
        parentId: task.parentId,
        status: task.status,
        priority: task.priority,
        startTime: task.startTime ? task.startTime.slice(0, 16) : null,
        endTime: task.endTime ? task.endTime.slice(0, 16) : null,
        dueDate: task.dueDate,
        reminderMinutes: task.reminderMinutes,
      });
    } else if (!selectedTaskId) {
      // 新建模式，重置表单
      setForm({
        title: '',
        description: '',
        projectId: projects[0]?.id || '',
        milestoneId: null,
        parentId: null,
        status: 'todo',
        priority: 'medium',
        startTime: null,
        endTime: null,
        dueDate: null,
        reminderMinutes: null,
      });
    }
  }, [taskData, selectedTaskId, projects]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = {
        ...form,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
      };

      if (selectedTaskId) {
        await updateMutation.mutateAsync({ id: selectedTaskId, data });
      } else {
        const result = await createMutation.mutateAsync(data);
        if (result.success && result.data && uploadingFiles.length > 0) {
          // 上传附件
          for (const file of uploadingFiles) {
            await uploadMutation.mutateAsync({ taskId: result.data.id, file });
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadingFiles(Array.from(e.target.files));
    }
  };

  const handleRemoveUploadingFile = (index: number) => {
    setUploadingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!isTaskModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {selectedTaskId ? '编辑任务' : '新建任务'}
          </h2>
          <button
            onClick={closeTaskModal}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            {/* 标题 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                任务标题 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title || ''}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="输入任务标题"
                required
              />
            </div>

            {/* 描述 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                描述
              </label>
              <textarea
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                placeholder="输入任务描述"
              />
            </div>

            {/* 项目和里程碑 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  项目 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Folder className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={form.projectId || ''}
                    onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">选择项目</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  里程碑
                </label>
                <div className="relative">
                  <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={form.milestoneId || ''}
                    onChange={(e) =>
                      setForm({ ...form, milestoneId: e.target.value || null })
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">无</option>
                    {milestones.map((milestone) => (
                      <option key={milestone.id} value={milestone.id}>
                        {milestone.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* 状态和优先级 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  状态
                </label>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm({ ...form, status: e.target.value as Task['status'] })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="todo">待办</option>
                  <option value="in_progress">进行中</option>
                  <option value="completed">已完成</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  优先级
                </label>
                <div className="relative">
                  <Flag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={form.priority}
                    onChange={(e) =>
                      setForm({ ...form, priority: e.target.value as Task['priority'] })
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">低</option>
                    <option value="medium">中</option>
                    <option value="high">高</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 时间 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  开始时间
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="datetime-local"
                    value={form.startTime || ''}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  结束时间
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="datetime-local"
                    value={form.endTime || ''}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* 截止日期和提醒 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  截止日期
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={form.dueDate || ''}
                    onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  提前提醒（分钟）
                </label>
                <input
                  type="number"
                  value={form.reminderMinutes || ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reminderMinutes: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例如：30"
                  min="0"
                />
              </div>
            </div>

            {/* 附件 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                附件
              </label>
              
              {/* 已有附件 */}
              {attachments.length > 0 && (
                <div className="mb-3 space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                    >
                      <Paperclip className="w-4 h-4 text-gray-400" />
                      <span className="flex-1 text-sm text-gray-700 truncate">
                        {attachment.originalName}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteAttachmentMutation.mutate(attachment.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* 上传新附件 */}
              {!selectedTaskId && (
                <div>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    <Plus className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">点击上传附件</span>
                  </label>
                  
                  {/* 待上传文件列表 */}
                  {uploadingFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {uploadingFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg"
                        >
                          <Paperclip className="w-4 h-4 text-blue-500" />
                          <span className="flex-1 text-sm text-gray-700 truncate">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveUploadingFile(index)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-all"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </form>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={closeTaskModal}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !form.title || !form.projectId}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {selectedTaskId ? '保存' : '创建'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 导入X图标
import { X as XIcon } from 'lucide-react';
const X = XIcon;
